// ============================================================
// Tiny Patio v2 — Shopify theme glue
// Cart drawer (optimistic), inline search, mobile nav, toast
// ============================================================
(function () {
  'use strict';

  const CART_URL = '/cart.js';
  const ADD_URL = '/cart/add.js';
  const CHANGE_URL = '/cart/change.js';
  const SUGGEST_URL = '/search/suggest.json';

  const drawer = () => document.getElementById('basket-drawer');
  const scrim = () => document.getElementById('basket-scrim');

  // ─── Cached cart state ─────────────────────────────────────
  // Updated optimistically before the server confirms, so the UI
  // stays responsive even when clicks come in faster than the API.
  let cartCache = null;
  let renderToken = 0;          // bumps every time a fresh server response arrives
  const pendingByKey = new Map();   // key -> { qty, controller, timer, seq }
  let seqCounter = 0;

  // ─── Artist names for cart lines ───────────────────────────
  // /cart.js exposes `vendor` but no metafields, and the artist lives in the
  // `tinypatio.artist` metaobject list — so the drawer's "by …" line has to come from
  // Liquid. snippets/cart-artists-seed.liquid prints the map for the cart as it was on
  // page load; sections/cart-artists.liquid re-renders it on demand for handles added since.
  const artistByHandle = new Map();
  let artistFetch = null;

  function mergeArtistJSON(node) {
    if (!node) return;
    try {
      const map = JSON.parse(node.textContent || '{}');
      Object.keys(map).forEach(h => artistByHandle.set(h, map[h]));
    } catch (e) { /* malformed map — the "by …" line is simply left off */ }
  }
  mergeArtistJSON(document.querySelector('script[data-cart-artists]'));

  // Artist for a cart line. Nothing is printed until the map has the handle: `line.vendor`
  // is the shop name here, not the maker, so flashing it would show the wrong credit. The
  // seed covers the cart as it loaded; only a just-added product waits on the fetch below.
  function artistFor(line) {
    return (line.handle && artistByHandle.get(line.handle)) || '';
  }

  // Pull the map for handles we haven't seen yet, then patch the rendered lines in place.
  async function refreshCartArtists(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    const missing = cart.items.some(l => l.handle && !artistByHandle.has(l.handle));
    if (!missing) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root_url) || '/';
    const url = root.replace(/\/$/, '') + '/?section_id=cart-artists';
    try {
      if (artistFetch) artistFetch.abort();
      artistFetch = new AbortController();
      const r = await fetch(url, { signal: artistFetch.signal, headers: { Accept: 'text/html' } });
      if (!r.ok) return;
      const tmp = document.createElement('div');
      tmp.innerHTML = await r.text();
      mergeArtistJSON(tmp.querySelector('script[data-cart-artists]'));
    } catch (e) { return; /* aborted or offline — the line just stays without a credit */ }

    document.querySelectorAll('.basket-drawer .line[data-line-handle]').forEach(row => {
      const name = artistByHandle.get(row.getAttribute('data-line-handle'));
      if (!name) return;
      let by = row.querySelector('.line-body .by');
      if (by) { by.textContent = 'by ' + name; return; }
      const title = row.querySelector('.line-body .title');
      if (!title) return;
      by = document.createElement('p');
      by.className = 'by';
      by.textContent = 'by ' + name;
      title.insertAdjacentElement('afterend', by);
    });
  }

  // ─── Toast ────────────────────────────────────────────────
  let toastTimer;
  function toast(msg) {
    let el = document.getElementById('tp-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'tp-toast';
      el.className = 'toast';
      document.body.appendChild(el);
    }
    el.textContent = msg;
    requestAnimationFrame(() => el.classList.add('show'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('show'), 2200);
  }

  // ─── Format money ─────────────────────────────────────────
  function formatMoney(cents) {
    const sym = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active === 'USD') ? '$' : '£';
    return sym + (cents / 100).toFixed(2);
  }

  // ─── Drawer open/close ─────────────────────────────────────
  function openDrawer() {
    const d = drawer(); const s = scrim();
    if (!d || !s) return;
    d.classList.add('open');
    d.setAttribute('aria-hidden', 'false');
    s.classList.add('open');
    document.body.classList.add('drawer-open');
  }
  function closeDrawer() {
    const d = drawer(); const s = scrim();
    if (!d) return;
    d.classList.remove('open');
    d.setAttribute('aria-hidden', 'true');
    if (s) s.classList.remove('open');
    document.body.classList.remove('drawer-open');
  }

  // ─── Free-shipping bar markup ──────────────────────────────
  function freeShippingHTML(totalCents) {
    const threshold = (window.TP_CONFIG && window.TP_CONFIG.free_shipping_threshold_cents) || 0;
    const showBar = !(window.TP_CONFIG && window.TP_CONFIG.show_free_shipping_bar === false);
    if (!showBar || threshold <= 0) return '';
    const qualified = totalCents >= threshold;
    const remaining = qualified ? 0 : threshold - totalCents;
    const pct = Math.max(0, Math.min(100, (totalCents / threshold) * 100));
    const msg = qualified
      ? (window.TP_STRINGS.free_shipping_qualified || '✦ Free shipping included.')
      : (window.TP_STRINGS.free_shipping_progress || 'Add {{ remaining }} for free shipping.').replace('{{ remaining }}', formatMoney(remaining));
    return (
      '<div class="ship-bar' + (qualified ? ' qualified' : '') + '">' +
        '<p class="ship-bar__msg">' + msg + '</p>' +
        '<div class="ship-bar__track"><span class="ship-bar__fill" style="width:' + pct.toFixed(1) + '%"></span></div>' +
      '</div>'
    );
  }

  // ─── Upsell carousel (Section Rendering API) ───────────────
  // The upsell pool depends on the metafields of the products in the basket, which
  // /cart.js and /products/x.js don't expose. So the carousel is rendered server-side
  // by sections/cart-upsell.liquid and pulled in here whenever the cart changes. The
  // section already de-dupes, excludes in-cart items, and honours the limit; we just
  // drop its .tp-upsell node into every [data-upsell-slot] (drawer footer + cart page).
  let upsellAbort = null;
  let upsellTimer = null;
  function scheduleUpsell() {
    clearTimeout(upsellTimer);
    upsellTimer = setTimeout(refreshUpsell, 120);
  }
  async function refreshUpsell() {
    const slots = document.querySelectorAll('[data-upsell-slot]');
    if (!slots.length) return;
    if (window.TP_CONFIG && window.TP_CONFIG.cart_upsell_enabled === false) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root_url) || '/';
    const url = root.replace(/\/$/, '') + '/?section_id=cart-upsell';
    try {
      if (upsellAbort) upsellAbort.abort();
      upsellAbort = new AbortController();
      const r = await fetch(url, { signal: upsellAbort.signal, headers: { Accept: 'text/html' } });
      if (!r.ok) return;
      const tmp = document.createElement('div');
      tmp.innerHTML = await r.text();
      const node = tmp.querySelector('.tp-upsell');
      const html = node ? node.outerHTML : '';
      slots.forEach(s => { s.innerHTML = html; });
    } catch (e) { /* aborted or offline — leave the current carousel in place */ }
  }

  // ─── Render cart drawer ────────────────────────────────────
  function renderCart(cart) {
    if (!cart) return;
    cartCache = cart;
    renderToken++;
    const body = document.getElementById('basket-body');
    const foot = document.getElementById('basket-footer');
    if (!body || !foot) return;

    // Header count badge
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
      if (cart.item_count === 0) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    });

    // Page-cart free-shipping bar (server-rendered host node, refresh on change)
    document.querySelectorAll('[data-free-shipping-bar]').forEach(el => {
      el.innerHTML = freeShippingHTML(cart.total_price);
    });

    if (cart.items.length === 0) {
      body.innerHTML =
        '<div class="empty"><p>' + (window.TP_STRINGS.empty || 'Nothing here yet.') + '</p>' +
        '<small>' + (window.TP_STRINGS.empty_subtext || '') + '</small></div>';
      foot.innerHTML = '<a class="btn btn-block" href="/collections/the-collection">' +
        (window.TP_STRINGS.continue_shopping || 'Continue shopping') + '</a>';
      return;
    }

    body.innerHTML = cart.items.map(line => {
      const img = line.image
        ? '<img src="' + line.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2') + '" alt="" loading="lazy">'
        : '';
      const safeKey = String(line.key).replace(/"/g, '&quot;');
      const hasVariants = !line.product_has_only_default_variant;
      const variantLine = (line.variant_title && line.variant_title !== 'Default Title')
        ? '<p class="variant">' + escapeHtml(line.variant_title) +
            (hasVariants && line.handle
              ? ' <button type="button" class="variant-change" data-variant-edit aria-label="Change variant">' +
                  (window.TP_STRINGS.change || 'Change') + '</button>'
              : '') +
          '</p>'
        : '';
      const handleAttr = line.handle ? ' data-line-handle="' + escapeHtml(line.handle) + '"' : '';
      const variantIdAttr = line.variant_id ? ' data-line-variant-id="' + line.variant_id + '"' : '';
      const decDisabled = line.quantity <= 1 ? ' disabled' : '';
      const incDisabled = line.quantity >= lineMaxQty(line) ? ' disabled' : '';
      return '<div class="line" data-line-key="' + safeKey + '"' + handleAttr + variantIdAttr + '>' +
        '<div class="thumb">' + img + '</div>' +
        '<div class="line-body">' +
          '<p class="title">' + line.product_title + '</p>' +
          (artistFor(line) ? '<p class="by">by ' + escapeHtml(artistFor(line)) + '</p>' : '') +
          variantLine +
          '<div class="variant-editor" data-variant-editor hidden></div>' +
          '<span class="qty">' +
            '<button type="button" data-qty-dec aria-label="Decrease"' + decDisabled + '>−</button>' +
            '<span data-qty>' + line.quantity + '</span>' +
            '<button type="button" data-qty-inc aria-label="Increase"' + incDisabled + '>+</button>' +
          '</span>' +
          '<button type="button" class="remove" data-line-remove>' +
            (window.TP_STRINGS.remove || 'Remove') + '</button>' +
        '</div>' +
        '<span class="price" data-line-price>' + formatMoney(line.final_line_price) + '</span>' +
      '</div>';
    }).join('');

    foot.innerHTML =
      '<div data-upsell-slot></div>' +
      freeShippingHTML(cart.total_price) +
      '<div class="row">' +
        '<span class="label">' + (window.TP_STRINGS.subtotal || 'Subtotal') + '</span>' +
        '<span class="value" data-cart-subtotal>' + formatMoney(cart.total_price) + '</span>' +
      '</div>' +
      '<a class="btn btn-block" href="/checkout">' + (window.TP_STRINGS.checkout || 'Checkout') + '</a>';

    // Load product JSON for every line: pre-warms the variant editor for multi-variant
    // items (so "Change" opens instantly) and supplies stock data so the + button can be
    // capped at available inventory once the JSON arrives.
    cart.items.forEach(line => {
      if (!line.handle) return;
      loadProductJson(line.handle).then(() => {
        const row = document.querySelector('.basket-drawer .line[data-line-key="' + CSS.escape(String(line.key)) + '"]');
        if (row) applyQtyLimits(row, line);
      });
    });

    // Refresh the cart-aware upsell carousel (debounced) now the cart has changed.
    scheduleUpsell();

    // Fill in the "by …" line for any product whose artist we don't have yet.
    refreshCartArtists(cart);
  }

  // ─── Optimistic local update ───────────────────────────────
  // Updates DOM right away. Recomputes line price from unit price * qty,
  // recomputes subtotal, and refreshes the free-shipping bar.
  function optimisticUpdate(key, newQty) {
    if (!cartCache || !Array.isArray(cartCache.items)) return;
    const line = cartCache.items.find(l => String(l.key) === String(key));
    if (!line) return;
    const unit = line.final_price; // per-unit after line discounts
    line.quantity = newQty;
    line.final_line_price = unit * newQty;

    cartCache.item_count = cartCache.items.reduce((s, l) => s + (l === line ? newQty : l.quantity), 0);
    cartCache.total_price = cartCache.items.reduce((s, l) => s + l.final_line_price, 0);

    // Targeted DOM patches (no full innerHTML rebuild → no flicker, no scroll jump)
    const row = document.querySelector('.basket-drawer .line[data-line-key="' + CSS.escape(String(key)) + '"]');
    if (row) {
      const qtyEl = row.querySelector('[data-qty]');
      const priceEl = row.querySelector('[data-line-price]');
      if (qtyEl) qtyEl.textContent = newQty;
      if (priceEl) priceEl.textContent = formatMoney(line.final_line_price);
      if (newQty === 0) row.classList.add('removing');
      else applyQtyLimits(row, line);
    }
    const sub = document.querySelector('[data-cart-subtotal]');
    if (sub) sub.textContent = formatMoney(cartCache.total_price);
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cartCache.item_count;
      if (cartCache.item_count === 0) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    });
    // Refresh free-shipping bars (drawer + page)
    document.querySelectorAll('[data-free-shipping-bar]').forEach(el => {
      el.innerHTML = freeShippingHTML(cartCache.total_price);
    });
    const drawerBar = document.querySelector('#basket-footer .ship-bar');
    if (drawerBar) drawerBar.outerHTML = freeShippingHTML(cartCache.total_price);
  }

  // ─── Debounced server sync ────────────────────────────────
  // Coalesce rapid clicks into one request per line. Latest qty wins.
  function scheduleChange(key) {
    const entry = pendingByKey.get(key);
    if (!entry) return;
    clearTimeout(entry.timer);
    entry.timer = setTimeout(() => flushChange(key), 220);
  }

  async function flushChange(key) {
    const entry = pendingByKey.get(key);
    if (!entry) return;
    // Cancel any in-flight request for this key
    if (entry.controller) entry.controller.abort();
    const controller = new AbortController();
    entry.controller = controller;
    const sentQty = entry.qty;
    const mySeq = ++seqCounter;
    entry.seq = mySeq;

    let cart = null;
    let needsRecovery = false;

    try {
      const r = await fetch(CHANGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: sentQty }),
        signal: controller.signal,
      });
      cart = await r.json().catch(() => null);
      if (!r.ok || !cart || !Array.isArray(cart.items)) needsRecovery = true;
    } catch (e) {
      if (e.name === 'AbortError') return; // superseded
      needsRecovery = true;
    }

    // Has a newer flushChange started? If so, bow out — let the newer one render.
    let cur = pendingByKey.get(key);
    if (!cur || cur.seq !== mySeq) return;

    // Server rejected the change or network failed. Refetch the authoritative cart
    // so cartCache never gets poisoned by a non-cart error response.
    if (needsRecovery) {
      try {
        const fresh = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
        const freshCart = await fresh.json();
        if (freshCart && Array.isArray(freshCart.items)) {
          cart = freshCart;
        } else {
          toast('Could not update basket.');
          return;
        }
      } catch (_) {
        toast('Could not update basket.');
        return;
      }
      // Re-check supersession after the recovery await.
      cur = pendingByKey.get(key);
      if (!cur || cur.seq !== mySeq) return;
    }

    // If the user clicked more while this request was in flight, their newer intent
    // (cur.qty) differs from what we just sent. Don't clobber the optimistic DOM with
    // stale server data and don't drop the pending entry — the scheduled next flush
    // will reconcile.
    if (cur.qty !== sentQty) return;

    pendingByKey.delete(key);
    renderCart(cart);
  }

  function changeLineQty(key, delta) {
    if (!cartCache || !Array.isArray(cartCache.items)) return;
    const line = cartCache.items.find(l => String(l.key) === String(key));
    if (!line) return;
    const existing = pendingByKey.get(key);
    const baseQty = existing ? existing.qty : line.quantity;
    // Stepper can't drop below 1 (Remove handles deletion) or exceed available stock.
    const newQty = Math.min(Math.max(1, baseQty + delta), lineMaxQty(line));
    if (newQty === baseQty) return;   // already at a limit — nothing to send
    pendingByKey.set(key, {
      qty: newQty,
      controller: existing ? existing.controller : null,
      timer: existing ? existing.timer : null,
      seq: existing ? existing.seq : 0,
    });
    optimisticUpdate(key, newQty);
    scheduleChange(key);
  }
  function removeLine(key) {
    if (!cartCache || !Array.isArray(cartCache.items)) return;
    const existing = pendingByKey.get(key) || {};
    pendingByKey.set(key, { qty: 0, controller: existing.controller || null, timer: existing.timer || null, seq: existing.seq || 0 });
    optimisticUpdate(key, 0);
    scheduleChange(key);
  }

  // ─── In-line variant switcher (cart drawer) ───────────────
  const productJsCache = new Map();      // handle -> Promise<product|null>
  const resolvedProducts = new Map();    // handle -> product|null (sync access once loaded)
  async function loadProductJson(handle) {
    if (productJsCache.has(handle)) return productJsCache.get(handle);
    const p = fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(r => r.ok ? r.json() : null)
      .catch(() => null);
    productJsCache.set(handle, p);
    p.then(prod => resolvedProducts.set(handle, prod || null));
    return p;
  }

  // Largest quantity a cart line may reach. Infinity when the variant isn't
  // inventory-tracked or allows overselling, or while product data is still loading
  // (the server clamps in that brief window; we re-cap once the JSON arrives).
  function lineMaxQty(line) {
    if (!line || !line.handle) return Infinity;
    const prod = resolvedProducts.get(line.handle);
    if (!prod || !Array.isArray(prod.variants)) return Infinity;
    const v = prod.variants.find(vv => vv.id === line.variant_id);
    if (!v) return Infinity;
    if (!v.inventory_management || v.inventory_policy === 'continue') return Infinity;
    if (typeof v.inventory_quantity !== 'number') return Infinity;
    return Math.max(0, v.inventory_quantity);
  }

  // Toggle a line's − / + buttons: no decreasing below 1 (use Remove instead),
  // no increasing past available stock.
  function applyQtyLimits(row, line) {
    if (!row || !line) return;
    const max = lineMaxQty(line);
    const dec = row.querySelector('[data-qty-dec]');
    const inc = row.querySelector('[data-qty-inc]');
    if (dec) dec.disabled = line.quantity <= 1;
    if (inc) inc.disabled = line.quantity >= max;
  }

  function renderVariantEditor(editor, product, selected, currentVariantId) {
    const findVariant = opts => product.variants.find(v => v.options.every((val, i) => val === opts[i])) || null;
    const isAvailable = (i, val) => product.variants.some(v =>
      v.available && v.options[i] === val && v.options.every((o, j) => j === i || o === selected[j])
    );
    const optionsHtml = product.options.map((opt, i) => {
      const optName = (opt && typeof opt === 'object') ? (opt.name || '') : opt;
      const seen = new Set();
      const values = [];
      product.variants.forEach(v => {
        const val = v.options[i];
        if (!seen.has(val)) { seen.add(val); values.push(val); }
      });
      const valsHtml = values.map(val => {
        const pressed = selected[i] === val;
        const unavail = !isAvailable(i, val);
        return '<button type="button" class="val' + (unavail ? ' unavailable' : '') + '"' +
          ' data-line-opt="' + i + '" data-line-val="' + escapeHtml(val) + '"' +
          ' aria-pressed="' + (pressed ? 'true' : 'false') + '">' + escapeHtml(val) + '</button>';
      }).join('');
      return '<div class="variant-editor-option">' +
        '<span class="label">' + escapeHtml(optName) + '</span>' +
        '<div class="values">' + valsHtml + '</div>' +
      '</div>';
    }).join('');
    const candidate = findVariant(selected);
    const sameAsCurrent = candidate && candidate.id === currentVariantId;
    const canConfirm = candidate && candidate.available && !sameAsCurrent;
    const actionsHtml =
      '<div class="variant-editor-actions">' +
        '<button type="button" class="variant-editor-cancel" data-variant-cancel>' +
          (window.TP_STRINGS.cancel || 'Cancel') + '</button>' +
        '<button type="button" class="variant-editor-confirm" data-variant-confirm' +
          (canConfirm ? '' : ' disabled') + '>' +
          (window.TP_STRINGS.update || 'Update') + '</button>' +
      '</div>';
    editor.innerHTML = optionsHtml + actionsHtml;
    editor.dataset.currentVariantId = currentVariantId;
    editor._product = product;
    editor._selected = selected;
  }

  // Preview a candidate variant in the cart drawer without mutating cartCache.
  // Updates: variant text, line image, line price, cart subtotal, free-shipping bar.
  function applyVariantPreview(row, variant) {
    const key = row.dataset.lineKey;
    const cachedLine = cartCache && cartCache.items.find(l => String(l.key) === String(key));
    if (!cachedLine || !variant) return;
    const variantP = row.querySelector('.variant');
    if (variantP) {
      const first = variantP.firstChild;
      if (first && first.nodeType === 3) first.nodeValue = (variant.title || '') + ' ';
      else variantP.insertBefore(document.createTextNode((variant.title || '') + ' '), variantP.firstChild);
    }
    const newLineTotal = variant.price * cachedLine.quantity;
    const newCartTotal = cartCache.total_price - cachedLine.final_line_price + newLineTotal;
    const priceEl = row.querySelector('[data-line-price]');
    if (priceEl) priceEl.textContent = formatMoney(newLineTotal);
    const imgEl = row.querySelector('.thumb img');
    if (imgEl && variant.featured_image && variant.featured_image.src) {
      imgEl.src = variant.featured_image.src.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2');
    }
    const sub = document.querySelector('[data-cart-subtotal]');
    if (sub) sub.textContent = formatMoney(newCartTotal);
    const drawerBar = document.querySelector('#basket-footer .ship-bar');
    if (drawerBar) drawerBar.outerHTML = freeShippingHTML(newCartTotal);
    document.querySelectorAll('[data-free-shipping-bar]').forEach(el => {
      el.innerHTML = freeShippingHTML(newCartTotal);
    });
  }

  // Repaint a line + cart totals from the unmutated cartCache, discarding any preview.
  function restoreLineFromCache(row) {
    const key = row.dataset.lineKey;
    const cachedLine = cartCache && cartCache.items.find(l => String(l.key) === String(key));
    if (!cachedLine) return;
    const variantP = row.querySelector('.variant');
    if (variantP) {
      const first = variantP.firstChild;
      const t = (cachedLine.variant_title && cachedLine.variant_title !== 'Default Title') ? cachedLine.variant_title + ' ' : '';
      if (first && first.nodeType === 3) first.nodeValue = t;
    }
    const priceEl = row.querySelector('[data-line-price]');
    if (priceEl) priceEl.textContent = formatMoney(cachedLine.final_line_price);
    const imgEl = row.querySelector('.thumb img');
    if (imgEl && cachedLine.image) imgEl.src = cachedLine.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2');
    const sub = document.querySelector('[data-cart-subtotal]');
    if (sub) sub.textContent = formatMoney(cartCache.total_price);
    const drawerBar = document.querySelector('#basket-footer .ship-bar');
    if (drawerBar) drawerBar.outerHTML = freeShippingHTML(cartCache.total_price);
    document.querySelectorAll('[data-free-shipping-bar]').forEach(el => {
      el.innerHTML = freeShippingHTML(cartCache.total_price);
    });
  }

  async function openLineVariantEditor(row) {
    const editor = row.querySelector('[data-variant-editor]');
    if (!editor) return;
    const handle = row.dataset.lineHandle;
    if (!handle) return;
    if (!editor.hidden) {
      restoreLineFromCache(row);
      editor.hidden = true;
      editor.innerHTML = '';
      return;
    }
    editor.hidden = false;
    editor.innerHTML = '<div class="variant-editor-loading">' +
      (window.TP_STRINGS.loading || 'Loading…') + '</div>';
    const product = await loadProductJson(handle);
    if (!product) {
      editor.innerHTML = '<p class="variant-editor-error">' +
        (window.TP_STRINGS.variant_load_error || 'Could not load variants.') + '</p>';
      return;
    }
    const currentVariantId = parseInt(row.dataset.lineVariantId, 10);
    const currentVariant = product.variants.find(v => v.id === currentVariantId) || product.variants[0];
    const selected = currentVariant ? currentVariant.options.slice() : [];
    renderVariantEditor(editor, product, selected, currentVariantId);
  }

  async function switchLineVariant(key, newVariantId) {
    const line = cartCache && cartCache.items.find(l => String(l.key) === String(key));
    if (!line) return;
    const qty = line.quantity;
    const oldIndex = cartCache.items.indexOf(line);
    const preKeys = new Set(cartCache.items.map(l => String(l.key)));
    try {
      const r1 = await fetch(CHANGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      if (!r1.ok) throw new Error('remove failed');
      const fd = new FormData();
      fd.append('id', newVariantId);
      fd.append('quantity', qty);
      const r2 = await fetch(ADD_URL, { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
      if (!r2.ok) {
        const err = await r2.json().catch(() => ({}));
        toast(err.description || 'Could not update.');
      }
    } catch (e) {
      toast('Could not update.');
    }
    const cart = await fetchCart();
    // Preserve line position: locate the resulting line for newVariantId and put it where
    // the old one was. Prefer a brand-new key (the just-added line); otherwise fall back
    // to any matching variant_id (a merge into an existing line).
    if (cart && Array.isArray(cart.items) && oldIndex >= 0) {
      let idx = cart.items.findIndex(l => l.variant_id === newVariantId && !preKeys.has(String(l.key)));
      if (idx < 0) idx = cart.items.findIndex(l => l.variant_id === newVariantId);
      if (idx >= 0 && idx !== oldIndex) {
        const [item] = cart.items.splice(idx, 1);
        cart.items.splice(Math.min(oldIndex, cart.items.length), 0, item);
      }
    }
    renderCart(cart);
  }

  async function fetchCart() {
    const r = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
    return r.json();
  }

  // ─── Add to cart ──────────────────────────────────────────
  async function addToCart(form) {
    const fromQuickAdd = !!form.closest('#quick-add-drawer');
    const data = new FormData(form);
    const btn = form.querySelector('[type="submit"], button:not([type])');
    if (btn) { btn.dataset.origText = btn.textContent; btn.disabled = true; btn.textContent = '…'; }
    try {
      const r = await fetch(ADD_URL, { method: 'POST', headers: { Accept: 'application/json' }, body: data });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast(err.description || 'Could not add to basket.');
        return;
      }
      const cart = await fetchCart();
      renderCart(cart);
      toast(window.TP_STRINGS.added || 'Added to basket.');
      // On the cart page the line items are server-rendered, so reload to reflect
      // the new line + totals rather than opening the (redundant) drawer over it.
      if (document.body.classList.contains('template-cart')) {
        if (fromQuickAdd) closeQuickAdd();
        window.location.reload();
        return;
      }
      if (fromQuickAdd) {
        closeQuickAdd();
        openDrawer();
      } else if (window.TP_CONFIG && window.TP_CONFIG.cart_drawer_open_on_add) {
        openDrawer();
      }
    } catch (e) {
      toast('Connection error.');
    } finally {
      if (btn) { btn.disabled = false; if (btn.dataset.origText) btn.textContent = btn.dataset.origText; }
    }
  }

  // ─── Upsell add-to-basket ──────────────────────────────────
  // Single-variant products are added straight to the basket; multi-variant
  // products open the quick-add drawer so the customer can pick options first.
  async function upsellAdd(btn) {
    const hasVariants = btn.dataset.upsellHasVariants === 'true';
    const handle = btn.dataset.upsellHandle;
    if (hasVariants) {
      if (handle) { openQuickAdd(); loadQuickAdd(handle); }
      return;
    }
    const variantId = btn.dataset.upsellVariantId;
    if (!variantId) return;
    const orig = btn.textContent;
    btn.disabled = true; btn.textContent = '…';
    try {
      const fd = new FormData();
      fd.append('id', variantId);
      fd.append('quantity', 1);
      const r = await fetch(ADD_URL, { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast(err.description || 'Could not add to basket.');
        return;
      }
      const cart = await fetchCart();
      renderCart(cart);
      toast(window.TP_STRINGS.added || 'Added to basket.');
      if (document.body.classList.contains('template-cart')) { window.location.reload(); return; }
    } catch (e) {
      toast('Connection error.');
    } finally {
      btn.disabled = false; btn.textContent = orig;
    }
  }

  // ─── Add multiple items at once ────────────────────────────
  // Used by the PDP "often bought together" bundle: posts an items[]
  // array to /cart/add.js in a single request, then refreshes the cart
  // UI (which also refreshes the upsell carousel) and opens the drawer.
  async function addItemsToCart(items, btn) {
    if (!items || !items.length) return;
    if (btn) { btn.dataset.origText = btn.textContent; btn.disabled = true; btn.textContent = '…'; }
    try {
      const r = await fetch(ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast(err.description || 'Could not add to basket.');
        return;
      }
      const cart = await fetchCart();
      renderCart(cart);
      toast(window.TP_STRINGS.added || 'Added to basket.');
      if (document.body.classList.contains('template-cart')) { window.location.reload(); return; }
      openDrawer();
    } catch (e) {
      toast('Connection error.');
    } finally {
      if (btn) { btn.disabled = false; if (btn.dataset.origText) btn.textContent = btn.dataset.origText; }
    }
  }

  // ─── Quick-add drawer ─────────────────────────────────────
  // Opens a side drawer with the product's variant pickers when a
  // collection card's "Choose options" CTA is clicked. Submits via
  // the standard data-cart-add path; on success the quick-add closes
  // and the cart drawer opens for confirmation.
  const qaDrawer = () => document.getElementById('quick-add-drawer');
  const qaScrim = () => document.getElementById('quick-add-scrim');
  let qaProduct = null;
  let qaSelected = [];
  // Metafield-backed extras handed over by the trigger element. /products/<handle>.js
  // returns no metafields, so artist name and rating can only reach the popup this way —
  // the Liquid that rendered the card puts them on the link as data-*.
  let qaMeta = { shortDesc: '', artist: '', rating: null, ratingMax: 5, ratingCount: 0 };

  function openQuickAdd() {
    const d = qaDrawer(); const s = qaScrim();
    if (!d || !s) return;
    d.classList.add('open');
    d.setAttribute('aria-hidden', 'false');
    s.classList.add('open');
    document.body.classList.add('drawer-open');
  }
  function closeQuickAdd() {
    const d = qaDrawer(); const s = qaScrim();
    if (!d) return;
    d.classList.remove('open');
    d.setAttribute('aria-hidden', 'true');
    if (s) s.classList.remove('open');
    // Release body scroll lock only if the cart drawer isn't also open
    const cd = drawer();
    if (!cd || !cd.classList.contains('open')) {
      document.body.classList.remove('drawer-open');
    }
  }

  async function loadQuickAdd(handle, data) {
    const body = document.getElementById('quick-add-body');
    if (!body) return;
    const d = data || {};
    const rating = parseFloat(d.rating);
    const count = parseInt(d.ratingCount, 10);
    qaMeta = {
      shortDesc: d.shortDesc || '',
      artist: d.artist || '',
      rating: isNaN(rating) ? null : rating,
      ratingMax: parseFloat(d.ratingMax) || 5,
      ratingCount: isNaN(count) ? 0 : count
    };
    body.innerHTML = '<div class="quick-add-loading">Loading…</div>';
    try {
      const r = await fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } });
      if (!r.ok) throw new Error('Fetch failed');
      const product = await r.json();
      qaProduct = product;
      const initial = product.variants.find(v => v.available) || product.variants[0];
      qaSelected = initial ? initial.options.slice() : [];
      renderQuickAdd();
    } catch (e) {
      body.innerHTML = '<div class="quick-add-error">Could not load product. Please try again.</div>';
    }
  }

  function findQaVariant(opts) {
    if (!qaProduct) return null;
    return qaProduct.variants.find(v => v.options.every((val, i) => val === opts[i])) || null;
  }
  // Is there an available variant with `value` at `optionIndex`, given the other currently-selected options?
  function isQaValueAvailable(optionIndex, value) {
    if (!qaProduct) return false;
    return qaProduct.variants.some(v => {
      if (!v.available) return false;
      if (v.options[optionIndex] !== value) return false;
      return v.options.every((val, i) => i === optionIndex || val === qaSelected[i]);
    });
  }

  function renderQuickAdd() {
    const body = document.getElementById('quick-add-body');
    if (!body || !qaProduct) return;
    const variant = findQaVariant(qaSelected);
    const variantImg = variant && variant.featured_image && variant.featured_image.src;
    const featured = variantImg || qaProduct.featured_image || '';
    const imgUrl = featured ? (featured.indexOf('//') === 0 ? 'https:' + featured : featured) : '';
    const imgSized = imgUrl ? imgUrl.replace(/(\.[a-z]+)(\?|$)/i, '_240x$1$2') : '';

    const heroHtml =
      '<div class="quick-add-hero">' +
        '<div class="thumb">' + (imgSized ? '<img src="' + imgSized + '" alt="" loading="lazy">' : '') + '</div>' +
        '<div>' +
          '<p class="title">' + escapeHtml(qaProduct.title) + '</p>' +
          (qaMeta.artist ? '<p class="artist">by ' + escapeHtml(qaMeta.artist) + '</p>' : '') +
          '<p class="price">' +
            (variant ? formatMoney(variant.price) : formatMoney(qaProduct.price)) +
            (variant && variant.compare_at_price && variant.compare_at_price > variant.price
              ? '<span class="compare">' + formatMoney(variant.compare_at_price) + '</span>' : '') +
          '</p>' +
          starsHtml(qaMeta.rating, qaMeta.ratingMax, qaMeta.ratingCount) +
        '</div>' +
      '</div>';

    // Single-variant ("simple") products have no real options — Shopify reports a
    // lone "Title / Default Title" variant. Skip the picker so the popup reads as a
    // clean quick-view + add rather than a pointless one-value option row.
    const firstOptName = qaProduct.options.length
      ? ((typeof qaProduct.options[0] === 'object') ? qaProduct.options[0].name : qaProduct.options[0])
      : '';
    const isDefaultOnly = qaProduct.variants.length === 1 &&
      (qaProduct.variants[0].title === 'Default Title' || firstOptName === 'Title');

    const optionsHtml = isDefaultOnly ? '' : qaProduct.options.map((opt, i) => {
      const optName = (opt && typeof opt === 'object') ? (opt.name || '') : opt;
      const seen = new Set();
      const values = [];
      qaProduct.variants.forEach(v => {
        const val = v.options[i];
        if (!seen.has(val)) { seen.add(val); values.push(val); }
      });
      const valuesHtml = values.map(val => {
        const pressed = qaSelected[i] === val;
        const unavailable = !isQaValueAvailable(i, val);
        return '<button type="button" class="val' + (unavailable ? ' unavailable' : '') + '"' +
          ' data-qa-option="' + i + '" data-qa-value="' + escapeHtml(val) + '"' +
          ' aria-pressed="' + (pressed ? 'true' : 'false') + '">' + escapeHtml(val) + '</button>';
      }).join('');
      return '<div class="quick-add-option">' +
        '<span class="label">' + escapeHtml(optName) +
          (qaSelected[i] ? '<span class="selected">' + escapeHtml(qaSelected[i]) + '</span>' : '') +
        '</span>' +
        '<div class="values">' + valuesHtml + '</div>' +
      '</div>';
    }).join('');

    const available = !!(variant && variant.available);
    const btnLabel = !variant ? 'Unavailable' :
      (available ? (window.TP_STRINGS.add_to_cart || 'Add to basket') : (window.TP_STRINGS.sold_out || 'Sold out'));
    const stockText = !variant ? 'Unavailable' :
      (available ? (window.TP_STRINGS.in_stock || 'In stock') : (window.TP_STRINGS.sold_out || 'Sold out'));

    const footerHtml =
      '<div class="quick-add-footer">' +
        '<div class="stock-row' + (available ? '' : ' out') + '">' +
          '<span><span class="dot"></span>' + stockText + '</span>' +
          '<a class="view-full" href="' + qaProduct.url + '">View full details →</a>' +
        '</div>' +
        '<form data-cart-add method="post" action="/cart/add">' +
          '<input type="hidden" name="id" value="' + (variant ? variant.id : '') + '">' +
          '<input type="hidden" name="quantity" value="1">' +
          '<button type="submit" class="btn-add"' + (available ? '' : ' disabled') + '>' + btnLabel + '</button>' +
        '</form>' +
      '</div>';

    // Short blurb: prefer the value passed in from the card (tinypatio.short_description);
    // otherwise derive one from the full description so every popup gets a blurb.
    const blurb = qaMeta.shortDesc || plainText(qaProduct.description, 30);
    const descHtml = blurb ? '<p class="quick-add-desc">' + escapeHtml(blurb) + '</p>' : '';

    body.innerHTML = heroHtml + descHtml + optionsHtml + footerHtml;
  }

  // ─── Mobile nav drawer ────────────────────────────────────
  function openMobileNav() {
    const d = document.getElementById('MobileNav');
    const s = document.querySelector('.mobile-nav-scrim');
    const t = document.querySelector('[data-mobile-nav-open]');
    if (!d || !s) return;
    s.hidden = false;
    requestAnimationFrame(() => { d.classList.add('open'); s.classList.add('open'); });
    d.setAttribute('aria-hidden', 'false');
    if (t) t.setAttribute('aria-expanded', 'true');
    document.body.classList.add('menu-open');
  }
  function closeMobileNav() {
    const d = document.getElementById('MobileNav');
    const s = document.querySelector('.mobile-nav-scrim');
    const t = document.querySelector('[data-mobile-nav-open]');
    if (!d || !s) return;
    d.classList.remove('open'); s.classList.remove('open');
    d.setAttribute('aria-hidden', 'true');
    if (t) t.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('menu-open');
    setTimeout(() => { s.hidden = true; }, 280);
  }

  // ─── Inline predictive search ─────────────────────────────
  let searchAbort = null;
  let searchTimer = null;

  function openSearch() {
    const panel = document.getElementById('tp-search-panel');
    if (!panel) return;
    panel.classList.add('open');
    panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('search-open');
    const input = panel.querySelector('input[type="search"]');
    if (input) setTimeout(() => input.focus(), 80);
  }
  function closeSearch() {
    const panel = document.getElementById('tp-search-panel');
    if (!panel) return;
    panel.classList.remove('open');
    panel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('search-open');
    if (searchAbort) searchAbort.abort();
  }
  async function runSearch(q) {
    const results = document.getElementById('tp-search-results');
    if (!results) return;
    const trimmed = (q || '').trim();
    if (trimmed.length < 2) {
      results.innerHTML = '';
      return;
    }
    if (searchAbort) searchAbort.abort();
    searchAbort = new AbortController();
    results.classList.add('loading');
    try {
      const url = SUGGEST_URL + '?q=' + encodeURIComponent(trimmed) +
        '&resources[type]=product,article,page&resources[limit]=6&resources[options][unavailable_products]=last';
      const r = await fetch(url, { signal: searchAbort.signal, headers: { Accept: 'application/json' } });
      const data = await r.json();
      const products = (data.resources && data.resources.results && data.resources.results.products) || [];
      const articles = (data.resources && data.resources.results && data.resources.results.articles) || [];
      const pages = (data.resources && data.resources.results && data.resources.results.pages) || [];
      if (!products.length && !articles.length && !pages.length) {
        results.innerHTML = '<p class="tp-search-empty">' +
          (window.TP_STRINGS.search_empty || 'No matches for') + ' "<strong>' + escapeHtml(trimmed) + '</strong>"</p>';
      } else {
        let html = '';
        if (products.length) {
          html += '<h4 class="tp-search-group">' + (window.TP_STRINGS.search_products || 'Products') + '</h4><ul class="tp-search-list">';
          html += products.map(p => {
            const img = p.featured_image && p.featured_image.url
              ? '<img src="' + p.featured_image.url + '&width=120" alt="" loading="lazy">'
              : '<span class="tp-search-thumb-placeholder"></span>';
            const price = p.price ? '<span class="tp-search-price">' + p.price + '</span>' : '';
            return '<li><a href="' + p.url + '">' +
              '<span class="tp-search-thumb">' + img + '</span>' +
              '<span class="tp-search-text"><span class="tp-search-title">' + escapeHtml(p.title) + '</span>' + price + '</span>' +
              '</a></li>';
          }).join('');
          html += '</ul>';
        }
        if (articles.length) {
          html += '<h4 class="tp-search-group">' + (window.TP_STRINGS.search_articles || 'Journal') + '</h4><ul class="tp-search-list tp-search-list--text">';
          html += articles.map(a => '<li><a href="' + a.url + '">' + escapeHtml(a.title) + '</a></li>').join('');
          html += '</ul>';
        }
        if (pages.length) {
          html += '<h4 class="tp-search-group">' + (window.TP_STRINGS.search_pages || 'Pages') + '</h4><ul class="tp-search-list tp-search-list--text">';
          html += pages.map(p => '<li><a href="' + p.url + '">' + escapeHtml(p.title) + '</a></li>').join('');
          html += '</ul>';
        }
        html += '<a class="tp-search-all" href="/search?q=' + encodeURIComponent(trimmed) + '">' +
          (window.TP_STRINGS.search_all || 'View all results') + ' →</a>';
        results.innerHTML = html;
      }
    } catch (e) {
      if (e.name !== 'AbortError') {
        results.innerHTML = '<p class="tp-search-empty">Search unavailable. Try again.</p>';
      }
    } finally {
      results.classList.remove('loading');
    }
  }
  // Star rating row. Must stay byte-comparable with snippets/star-rating.liquid so a card
  // and the popup it opens never render the same product's stars differently.
  // Returns '' for an unrated product, so callers can concatenate it unconditionally.
  function starsHtml(rating, max, count) {
    if (rating == null || !(count > 0)) return '';
    const scale = max || 5;
    const pct = Math.round(Math.max(0, Math.min(rating / scale, 1)) * 1000) / 10;
    const label = 'Rated ' + rating.toFixed(1) + ' out of ' + Math.round(scale) +
      ' from ' + count + ' review' + (count === 1 ? '' : 's');
    return '<span class="tp-stars" role="img" aria-label="' + escapeHtml(label) + '">' +
      '<span class="tp-stars__bar" aria-hidden="true"><span class="tp-stars__on" style="width: ' + pct + '%;"></span></span>' +
      '<span class="tp-stars__n" aria-hidden="true">(' + count + ')</span>' +
    '</span>';
  }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }
  // Strip HTML and trim to `words` words (with an ellipsis) — used to derive a
  // short blurb from a product's full description when no short_description is given.
  function plainText(html, words) {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const text = (tmp.textContent || '').replace(/\s+/g, ' ').trim();
    if (!words) return text;
    const parts = text.split(' ');
    return parts.length > words ? parts.slice(0, words).join(' ') + '…' : text;
  }

  // ─── Init ──────────────────────────────────────────────────
  function init() {
    // Cart open — open instantly using cached state, refresh in background
    document.querySelectorAll('[data-cart-open]').forEach(el => {
      el.addEventListener('click', e => {
        e.preventDefault();
        if (cartCache) renderCart(cartCache); // paint with what we have
        openDrawer();                          // open immediately
        fetchCart().then(renderCart).catch(() => {});
      });
    });
    // Cart close
    document.querySelectorAll('[data-cart-close]').forEach(el => el.addEventListener('click', closeDrawer));
    if (scrim()) scrim().addEventListener('click', closeDrawer);

    // Delegated handlers so re-rendered DOM nodes keep working
    const bodyEl = document.getElementById('basket-body');
    if (bodyEl) {
      bodyEl.addEventListener('click', e => {
        const incBtn = e.target.closest('[data-qty-inc]');
        const decBtn = e.target.closest('[data-qty-dec]');
        const rmBtn  = e.target.closest('[data-line-remove]');
        const editBtn = e.target.closest('[data-variant-edit]');
        const optBtn = e.target.closest('[data-line-opt]');
        const confirmBtn = e.target.closest('[data-variant-confirm]');
        const cancelBtn = e.target.closest('[data-variant-cancel]');
        const line = e.target.closest('[data-line-key]');
        if (!line) return;
        const key = line.dataset.lineKey;
        if (incBtn) changeLineQty(key, +1);
        else if (decBtn) changeLineQty(key, -1);
        else if (rmBtn) removeLine(key);
        else if (editBtn) openLineVariantEditor(line);
        else if (cancelBtn) {
          const editor = cancelBtn.closest('[data-variant-editor]');
          if (editor) {
            restoreLineFromCache(line);
            editor.hidden = true;
            editor.innerHTML = '';
          }
        }
        else if (confirmBtn) {
          if (confirmBtn.hasAttribute('disabled')) return;
          const editor = confirmBtn.closest('[data-variant-editor]');
          if (!editor || !editor._product) return;
          const v = editor._product.variants.find(vv => vv.options.every((o, j) => o === editor._selected[j]));
          if (!v || v.id === parseInt(editor.dataset.currentVariantId, 10)) return;
          // DOM already shows the preview. Commit to cartCache so totals stay consistent,
          // then sync to server in background with a loading indicator on the row.
          const cachedLine = cartCache && cartCache.items.find(l => String(l.key) === String(key));
          if (cachedLine) {
            cachedLine.variant_id = v.id;
            cachedLine.variant_title = v.title;
            cachedLine.final_price = v.price;
            cachedLine.price = v.price;
            cachedLine.final_line_price = v.price * cachedLine.quantity;
            cachedLine.line_price = cachedLine.final_line_price;
            if (v.featured_image && v.featured_image.src) cachedLine.image = v.featured_image.src;
            cartCache.total_price = cartCache.items.reduce((s, l) => s + l.final_line_price, 0);
          }
          line.dataset.lineVariantId = v.id;
          line.classList.add('syncing');
          editor.hidden = true;
          editor.innerHTML = '';
          switchLineVariant(key, v.id).finally(() => line.classList.remove('syncing'));
        }
        else if (optBtn) {
          const editor = optBtn.closest('[data-variant-editor]');
          if (!editor || !editor._product) return;
          const i = parseInt(optBtn.dataset.lineOpt, 10);
          editor._selected[i] = optBtn.dataset.lineVal;
          renderVariantEditor(editor, editor._product, editor._selected, parseInt(editor.dataset.currentVariantId, 10));
          const candidate = editor._product.variants.find(vv => vv.options.every((o, j) => o === editor._selected[j]));
          if (candidate) applyVariantPreview(line, candidate);
          else restoreLineFromCache(line);
        }
      });
    }

    // Quick-add triggers (delegated — cards may be re-rendered)
    document.addEventListener('click', e => {
      const trigger = e.target.closest('[data-quick-add-trigger]');
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      const handle = trigger.dataset.productHandle;
      if (!handle) return;
      openQuickAdd();
      loadQuickAdd(handle, trigger.dataset);
    });
    // Upsell carousel (delegated — present in the drawer footer and on the cart page,
    // both re-rendered on every cart change)
    document.addEventListener('click', e => {
      const addBtn = e.target.closest('[data-upsell-add]');
      if (addBtn) { e.preventDefault(); upsellAdd(addBtn); return; }
      const arrow = e.target.closest('[data-upsell-prev], [data-upsell-next]');
      if (arrow) {
        e.preventDefault();
        const wrap = arrow.closest('[data-upsell]');
        const track = wrap && wrap.querySelector('[data-upsell-track]');
        if (!track) return;
        const item = track.querySelector('.tp-upsell__item');
        const step = item ? item.offsetWidth + 10 : track.clientWidth * 0.8;
        track.scrollBy({ left: arrow.hasAttribute('data-upsell-prev') ? -step : step, behavior: 'smooth' });
      }
    });

    // Quick-add close
    document.querySelectorAll('[data-quick-add-close]').forEach(el => el.addEventListener('click', closeQuickAdd));
    if (qaScrim()) qaScrim().addEventListener('click', closeQuickAdd);
    // Quick-add option clicks (delegated — body is re-rendered on every select)
    const qaBody = document.getElementById('quick-add-body');
    if (qaBody) {
      qaBody.addEventListener('click', e => {
        const btn = e.target.closest('[data-qa-option]');
        if (!btn) return;
        const idx = parseInt(btn.dataset.qaOption, 10);
        qaSelected[idx] = btn.dataset.qaValue;
        renderQuickAdd();
      });
    }

    // Keyboard
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') { closeQuickAdd(); closeDrawer(); closeMobileNav(); closeSearch(); }
    });

    // Mobile nav
    document.querySelectorAll('[data-mobile-nav-open]').forEach(el => el.addEventListener('click', e => { e.preventDefault(); openMobileNav(); }));
    document.querySelectorAll('[data-mobile-nav-close]').forEach(el => el.addEventListener('click', closeMobileNav));

    // Mobile submenu expand / collapse
    const mNav = document.querySelector('#MobileNav .m-nav');
    if (mNav) {
      mNav.addEventListener('click', e => {
        const btn = e.target.closest('[data-mobile-submenu-toggle]');
        if (!btn) return;
        e.preventDefault();
        const wrapper = btn.closest('.m-nav__item--has-children, .m-nav__sub-item--has-children');
        if (!wrapper) return;
        const sub = wrapper.querySelector(':scope > .m-nav__sub');
        if (!sub) return;
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        if (isOpen) sub.setAttribute('hidden', '');
        else sub.removeAttribute('hidden');
      });
    }

    // Inline search
    document.querySelectorAll('[data-search-open]').forEach(el => el.addEventListener('click', e => { e.preventDefault(); openSearch(); }));
    document.querySelectorAll('[data-search-close]').forEach(el => el.addEventListener('click', e => { e.preventDefault(); closeSearch(); }));
    const searchInput = document.querySelector('#tp-search-panel input[type="search"]');
    if (searchInput) {
      searchInput.addEventListener('input', e => {
        clearTimeout(searchTimer);
        const val = e.target.value;
        searchTimer = setTimeout(() => runSearch(val), 180);
      });
    }
    const searchForm = document.querySelector('#tp-search-panel form');
    if (searchForm) {
      searchForm.addEventListener('submit', e => {
        // Let it submit to /search for the full results page.
      });
    }

    // Intercept add-to-cart forms
    document.addEventListener('submit', e => {
      const form = e.target.closest('form[data-cart-add]');
      if (!form) return;
      e.preventDefault();
      addToCart(form);
    });

    // Drop a subtle shadow on the header once the user scrolls
    const headerEl = document.querySelector('.tp-header');
    if (headerEl) {
      const onScroll = () => {
        if (window.scrollY > 6) headerEl.classList.add('scrolled');
        else headerEl.classList.remove('scrolled');
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    // Warm the cart cache so the drawer can open without waiting
    fetchCart().then(cart => { cartCache = cart; renderCart(cart); }).catch(() => {});

    // Record + render "Recently viewed" once the consent API is ready, and re-run if the
    // visitor changes their choice (no reload needed). See initRecentlyViewed for gating.
    rvBootstrapRecentlyViewed();
  }

  // ─── Recently viewed ───────────────────────────────────────
  // Personal browsing history, so it lives in localStorage rather than on the server.
  // The host section (sections/recently-viewed.liquid) renders an empty grid; here we
  // record the current product and hydrate the grid from stored handles via the Section
  // Rendering API (one /products/{handle}?section_id=recently-viewed-card fetch per card,
  // mirroring how refreshUpsell() pulls in the cart-upsell carousel).
  //
  // Consent: localStorage is covered by UK PECR / GDPR the same as cookies, and "recently
  // viewed" is a functional/preferences feature (not strictly necessary). So we gate it on
  // Shopify's consent API — preferencesProcessingAllowed(). When no consent API is present
  // (merchant runs no banner), it behaves as a normal functional feature.
  const RV_KEY = 'tp:recently-viewed';
  const RV_MAX_STORED = 12;

  function rvRead() {
    try {
      const v = JSON.parse(localStorage.getItem(RV_KEY) || '[]');
      return Array.isArray(v) ? v.filter(h => typeof h === 'string' && h) : [];
    } catch (e) { return []; }
  }
  function rvWrite(list) {
    try { localStorage.setItem(RV_KEY, JSON.stringify(list)); } catch (e) { /* private mode / full — skip */ }
  }

  async function renderRecentlyViewed(host, handles) {
    const grid = host.querySelector('[data-rv-grid]');
    if (!grid || !handles.length) return;
    const limit = parseInt(host.getAttribute('data-rv-limit'), 10) || 4;
    const show = handles.slice(0, limit);
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root_url) || '/';
    const base = root.replace(/\/$/, '');
    const cards = await Promise.all(show.map(async h => {
      try {
        const r = await fetch(base + '/products/' + encodeURIComponent(h) + '?section_id=recently-viewed-card', { headers: { Accept: 'text/html' } });
        if (!r.ok) return '';
        const tmp = document.createElement('div');
        tmp.innerHTML = await r.text();
        const card = tmp.querySelector('.product-card');
        return card ? card.outerHTML : '';
      } catch (e) { return ''; }
    }));
    const html = cards.filter(Boolean).join('');
    if (!html) return;
    grid.innerHTML = html;
    host.removeAttribute('hidden');
  }

  // True when we may store/show recently-viewed. No consent API present (no banner
  // configured) → treat as a normal functional feature and allow. API present → respect
  // the visitor's choice for the "preferences" category.
  function rvConsentAllowed() {
    try {
      const cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.preferencesProcessingAllowed === 'function') {
        return cp.preferencesProcessingAllowed() === true;
      }
    } catch (e) { /* fall through */ }
    return true;
  }

  function initRecentlyViewed() {
    const host = document.querySelector('[data-recently-viewed]');

    // No consent (declined, revoked, or not yet given) → store nothing, show nothing,
    // and clear anything kept from an earlier consented session.
    if (!rvConsentAllowed()) {
      if (host) {
        host.setAttribute('hidden', '');
        const g = host.querySelector('[data-rv-grid]');
        if (g) g.innerHTML = '';
      }
      try { localStorage.removeItem(RV_KEY); } catch (e) {}
      return;
    }

    const current = host ? host.getAttribute('data-recently-viewed') : '';
    const list = rvRead();

    // Show the products viewed before this one (never the current product itself)
    if (host) renderRecentlyViewed(host, list.filter(h => h !== current));

    // Record the current product, most-recent-first, de-duped, capped
    if (current) {
      const next = [current].concat(list.filter(h => h !== current)).slice(0, RV_MAX_STORED);
      rvWrite(next);
    }
  }

  // Wait for the consent API to load (so we don't record in the brief window before a
  // banner app initialises), run once, then re-run whenever the visitor's consent changes.
  function rvBootstrapRecentlyViewed() {
    if (!document.querySelector('[data-recently-viewed]')) return;
    const start = function () {
      initRecentlyViewed();
      try {
        const cp = window.Shopify && window.Shopify.customerPrivacy;
        if (cp && typeof cp.subscribe === 'function') {
          cp.subscribe('visitorConsentCollected', initRecentlyViewed);
        } else {
          document.addEventListener('visitorConsentCollected', initRecentlyViewed);
        }
      } catch (e) {
        document.addEventListener('visitorConsentCollected', initRecentlyViewed);
      }
    };
    try {
      if (window.Shopify && window.Shopify.customerPrivacy) { start(); return; }
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], start);
        return;
      }
    } catch (e) { /* fall through to start without the API */ }
    start();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.TP = {
    openDrawer, closeDrawer, fetchCart, renderCart, addToCart, toast,
    openMobileNav, closeMobileNav, openSearch, closeSearch,
    openQuickAdd, closeQuickAdd, loadQuickAdd, addItems: addItemsToCart,
  };
})();
